<template>

<div class="mbh-results" v-if="data">

	<div class="mbh-intro" v-if="data.intro" v-html="parseSectionLinks(data.intro)" v-on:click="onSectionLinkClick"></div>

	<template v-if="hasData">

		<div class="mbh-results-quadrants" v-if="riskSection">

			<com-density :section="riskSection" :active="density !== false" :completed="true" />
			<com-risk :section="riskSection" :active="score !== null" :completed="true" :info="17" v-on:cates="showCates = true" v-on:graph="showGraph = true" />

			<div class="mbh-results-skeleton"></div>

		</div>

		<portal to="modal" v-if="showCates || showGraph">

			<div class="health-modal">

				<div class="health-modal-overlay" v-on:click="showCates = false; showGraph = false"></div>

				<div class="health-modal-panel">

					<div class="health-modal-header">
						<div class="health-modal-title"></div>
						<button class="health-modal-close" v-on:click="showCates = false; showGraph = false">&times;</button>
					</div>

					<div class="health-modal-body" style="flex-direction: column;">
						<com-cates v-if="showCates" :max="100" :bad="Math.abs(score)" title="Chance of breaking a bone" action="break a bone" />
						<div v-if="showGraph" style="display: flex; gap: 24px;">
							<com-chart :max="45" title="Major Fracture" type="m" />
							<com-chart :max="25" title="Hip" type="h" />
						</div>
						<div class="mbh-results-graph-explainer">
							<p>If we look at the picture, each coloured spot represents a person like you.</p>
							<p>For 100 people like you, the people shown in red are expected to break a bone over the next 10 years. So, for example, if there are 20 red spots, of 100 people like you, 20 of those people will be expected to break a bone over the next 10 years.</p>
							<p>The people in green will not break a bone. So, for example, if there are 80 green spots, of 100 people like you, 80 of those people will not break a bone over the next 10 years.</p>
							<p>However, we do not know which one of these 100 coloured spots will be you.</p>
						</div>
					</div>

				</div>

			</div>

		</portal>

	</template>

	<div class="mbh-nodata" v-if="!hasData">
		<i class="fa-solid fa-circle-info"></i>
		<div v-html="data.no_data || '<p>Some sections in My bone health are only available to people who have added personal health data to the tool.</p>'"></div>
	</div>

	<div class="mbh-explainers" v-if="data.frax || data.tscore || data.zscore">

		<div class="mbh-explainer" v-if="data.frax && data.frax.title" v-on:click="toggleExplainer('frax')" :class="{'is-active': explainer === 'frax'}">
			<div class="mbh-explainer-title">{{ data.frax.title }} <i class="fa-solid" :class="explainer === 'frax' ? 'fa-chevron-up' : 'fa-chevron-down'"></i></div>
			<div class="mbh-explainer-text" v-if="explainer === 'frax'" v-html="parseSectionLinks(data.frax.text)" v-on:click="onExplainerClick"></div>
		</div>

		<div class="mbh-explainer" v-if="data.tscore && data.tscore.title" v-on:click="toggleExplainer('tscore')" :class="{'is-active': explainer === 'tscore'}">
			<div class="mbh-explainer-title">{{ data.tscore.title }} <i class="fa-solid" :class="explainer === 'tscore' ? 'fa-chevron-up' : 'fa-chevron-down'"></i></div>
			<div class="mbh-explainer-text" v-if="explainer === 'tscore'" v-html="parseSectionLinks(data.tscore.text)" v-on:click="onExplainerClick"></div>
		</div>

		<div class="mbh-explainer" v-if="data.zscore && data.zscore.title" v-on:click="toggleExplainer('zscore')" :class="{'is-active': explainer === 'zscore'}">
			<div class="mbh-explainer-title">{{ data.zscore.title }} <i class="fa-solid" :class="explainer === 'zscore' ? 'fa-chevron-up' : 'fa-chevron-down'"></i></div>
			<div class="mbh-explainer-text" v-if="explainer === 'zscore'" v-html="parseSectionLinks(data.zscore.text)" v-on:click="onExplainerClick"></div>
		</div>

	</div>

</div>

</template>

<script>

import comDensity from './../risk/Density'
import comRisk from './../risk/Risk'
import comCates from './../common/Cates'
import comChart from './../common/Chart'

import sectionLinks from '@/mixin/sectionlinks'

export default {

	props: ['data'],

	mixins: [sectionLinks],

	components: {
		'com-density': comDensity,
		'com-risk': comRisk,
		'com-cates': comCates,
		'com-chart': comChart
	},

	data: function() {

		return {
			explainer: false,
			showCates: false,
			showGraph: false
		}

	},

	computed: {

		riskSection: function() {

			return this.$_.findWhere(this.$store.getters['data/aid/sections/all'], { type: 'risk' })

		},

		density: function() {

			if (!this.riskSection) return false
			var score = this.$store.getters['session/values'][this.riskSection.density.variable]
			return (score === null || score === undefined) ? false : parseFloat(score).toFixed(1)

		},

		score: function() {

			var s = this.frax('major')
			return (s !== 0) ? Math.round(s) : null

		},

		hasData: function() { return this.density !== false || this.score !== null }

	},

	methods: {

		toggleExplainer: function(key) { this.explainer = (this.explainer === key) ? false : key },

		// A [section=] link inside an explainer navigates away rather than
		// collapsing the explainer it was clicked in.
		onExplainerClick: function(e) {

			if (this.onSectionLinkClick(e)) e.stopPropagation()

		}

	}

}

</script>

<style>

.mbh-results .mbh-intro { font-size: 18px; color: #343434; line-height: 28px; margin-bottom: 24px; font-weight: 300; }

.mbh-results-quadrants {
	display: flex;
	flex-wrap: wrap;
	margin-bottom: 32px;
	border-radius: 16px;
}

.mbh-results-quadrants .density {
	width: 50%;
	border-radius: 16px 0 0 16px;
}

/* Left (density) quadrant keeps the original green. The patient theme
   switched --aid-color to orange app-wide, which caught this in the
   crossfire; restore green here only. Selector is specific enough to
   override Density.vue's scoped background rule. */
.mbh-results .mbh-results-quadrants .density {
	background-color: #5af2cf;
}

.mbh-results-quadrants .density.is-active {
	opacity: 1;
	filter: none;
}

.mbh-results-quadrants .riskf {
	width: 50%;
	border-radius: 0 16px 16px 0;
}

.mbh-results-quadrants .riskf.is-active {
	opacity: 1;
	filter: none;
}

.mbh-results-quadrants .density-next,
.mbh-results-quadrants .riskf-next { display: none; }

.is-mobile .mbh-results-quadrants {
	flex-direction: column;
}

.is-mobile .mbh-results-quadrants .density,
.is-mobile .mbh-results-quadrants .riskf {
	width: 100%;
	border-radius: 16px;
	margin-bottom: 8px;
}

.is-mobile .mbh-results-skeleton,
.is-tablet .mbh-results-skeleton { display: none; }

@media (max-width: 1449px) {
	.mbh-results-skeleton { display: none; }
}

.mbh-results-skeleton {
	position: absolute;
	top: -12px;
	left: calc(50% - 130px);
	width: 259px;
	height: 320px;
	background-image: url(~@/assets/aid/risk/skeleton.svg);
	background-size: contain;
	background-repeat: no-repeat;
	background-position: bottom center;
	z-index: 1;
	pointer-events: none;
}

.mbh-nodata { display: flex; align-items: flex-start; padding: 24px; background-color: #fff; border-radius: 12px; margin-bottom: 32px; font-size: 16px; color: #666; line-height: 24px; }
.mbh-nodata i { color: var(--aid-color); font-size: 20px; margin-right: 16px; margin-top: 2px; flex-shrink: 0; }

.mbh-results .mbh-explainers { margin-bottom: 16px; }
.mbh-results .mbh-explainer { border-bottom: 1px solid #e0e0e0; cursor: pointer; }
.mbh-results .mbh-explainer:first-child { border-top: 1px solid #e0e0e0; }
.mbh-results .mbh-explainer:hover { background-color: rgba(255,255,255,0.5); }
.mbh-results .mbh-explainer.is-active { background-color: rgba(255,255,255,0.5); }
.mbh-results .mbh-explainer-title { display: flex; justify-content: space-between; align-items: center; padding: 16px 20px; font-size: 16px; font-weight: 600; color: #343434; }
.mbh-results .mbh-explainer-title i { color: #999; font-size: 12px; }
.mbh-results .mbh-explainer-text { padding: 0 20px 20px; font-size: 16px; color: #343434; line-height: 26px; font-weight: 300; }
.mbh-results .mbh-explainer-text p { margin-bottom: 8px; }

.mbh-results-graph-explainer {
	background-color: var(--aid-color);
	border-radius: 17px;
	padding: 20px 40px;
	margin-top: 24px;
	font-size: 16px;
	line-height: 24px;
	color: #343434;
	font-weight: 600;
	width: 100%;
}

.mbh-results-graph-explainer p { margin-bottom: 8px; }
.mbh-results-graph-explainer p:last-child { margin-bottom: 0; }

</style>
